import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import { 
  ArrowLeft, Loader2, Star, Calendar, Sparkles, 
  Sun, Moon, TrendingUp, Heart, Briefcase, Home,
  Target, Shield, Zap, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ShubhHeader from '../components/shubh/ShubhHeader';
import ShubhBottomNav from '../components/shubh/ShubhBottomNav';

import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

const YEAR_THEMES = {
  career: { icon: Briefcase, color: 'from-blue-500 to-cyan-500', label: 'Career & Success' },
  love: { icon: Heart, color: 'from-pink-500 to-rose-500', label: 'Love & Relationships' },
  health: { icon: Shield, color: 'from-green-500 to-emerald-500', label: 'Health & Wellness' },
  wealth: { icon: TrendingUp, color: 'from-yellow-500 to-amber-500', label: 'Wealth & Finance' },
  family: { icon: Home, color: 'from-purple-500 to-violet-500', label: 'Family & Home' },
  spiritual: { icon: Sparkles, color: 'from-indigo-500 to-purple-500', label: 'Spiritual Growth' }
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const YearlyPredictionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [yearlyPrediction, setYearlyPrediction] = useState(null);
  const [activeCategory, setActiveCategory] = useState('overview');

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear + 1];

  useEffect(() => {
    fetchProfiles();
  }, []);

  useEffect(() => {
    if (selectedProfile && selectedYear) {
      fetchYearlyPrediction();
    }
  }, [selectedProfile, selectedYear]);

  const fetchProfiles = async () => {
    try {
      const response = await axios.get(`${API}/profiles`, { withCredentials: true });
      setProfiles(response.data);
      if (response.data.length > 0) {
        setSelectedProfile(response.data[0].profile_id);
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchYearlyPrediction = async () => {
    setGenerating(true);
    try {
      const response = await axios.get(
        `${API}/predictions/yearly/${selectedProfile}?year=${selectedYear}`,
        { withCredentials: true }
      );
      setYearlyPrediction(response.data);
    } catch (error) {
      console.error('Error fetching yearly prediction:', error);
      // Generate new prediction if not found
      await generateYearlyPrediction();
    } finally {
      setGenerating(false);
    }
  };

  const generateYearlyPrediction = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/predictions/yearly/generate`,
        { profile_id: selectedProfile, year: selectedYear },
        { withCredentials: true }
      );
      setYearlyPrediction(response.data);
    } catch (error) {
      console.error('Error generating yearly prediction:', error);
    } finally {
      setGenerating(false);
    }
  };

  const getSelectedProfileData = () => {
    return profiles.find(p => p.profile_id === selectedProfile);
  };

  if (loading) {
    return (
      <div className="min-h-screen cosmic-bg flex items-center justify-center">
        <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
        <div className="text-center relative z-10">
          <Star className="w-12 h-12 text-[#C84B32] animate-spin mx-auto mb-4" />
          <p className="text-[#6B6560]">Loading cosmic forecasts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen cosmic-bg pb-28">
      {/* Background effects */}
      <div className="fixed inset-0 stars-bg opacity-30 pointer-events-none" />
      <div className="fixed inset-0 nebula-bg pointer-events-none" />

      {/* Navigation */}
      <ShubhHeader title="Yearly Forecast" showBack />

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile & Year Selection */}
        <div className="cosmic-card p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <div className="w-full sm:w-48">
                <label className="text-[#6B6560] text-xs mb-1 block">Select Profile</label>
                <Select value={selectedProfile || ''} onValueChange={setSelectedProfile}>
                  <SelectTrigger className="bg-white border-[#282622]/10 text-[#282622]" data-testid="yearly-profile-select">
                    <SelectValue placeholder="Choose profile" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#282622]/10">
                    {profiles.map(profile => (
                      <SelectItem key={profile.profile_id} value={profile.profile_id} className="text-[#282622]">
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-full sm:w-36">
                <label className="text-[#6B6560] text-xs mb-1 block">Year</label>
                <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                  <SelectTrigger className="bg-white border-[#282622]/10 text-[#282622]" data-testid="yearly-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#282622]/10">
                    {years.map(year => (
                      <SelectItem key={year} value={String(year)} className="text-[#282622]">
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <Button
              onClick={generateYearlyPrediction}
              disabled={generating || !selectedProfile}
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white hover:from-[#E85D43] hover:to-[#F5B942]"
              data-testid="regenerate-yearly-btn"
            >
              {generating ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {generating ? 'Generating...' : 'Refresh Prediction'}
            </Button>
          </div>
        </div>

        {/* Loading State */}
        {generating && (
          <div className="cosmic-card p-12 text-center">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-[#C84B32]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-[#C84B32] border-t-transparent animate-spin" />
              <Star className="absolute inset-0 m-auto w-10 h-10 text-[#C84B32]" />
            </div>
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-2">
              Consulting the Stars
            </h3>
            <p className="text-[#6B6560]">
              Generating your {selectedYear} cosmic forecast...
            </p>
          </div>
        )}

        {/* Yearly Prediction Content */}
        {yearlyPrediction && !generating && (
          <AnimatePresence mode="wait">
            <motion.div
              key={`${selectedProfile}-${selectedYear}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="space-y-8"
            >
              {/* Year Overview Card */}
              <div className="cosmic-card p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#C84B32]/10 to-transparent rounded-full blur-3xl" />
                
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <Sun className="w-8 h-8 text-[#C84B32]" />
                    <h2 className="font-['Fraunces'] text-3xl text-[#282622]">
                      {selectedYear} Overview
                    </h2>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-6 mb-6">
                    <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                      <p className="text-[#6B6560] text-sm mb-1">Your Rashi</p>
                      <p className="text-[#C84B32] font-semibold text-lg">
                        {yearlyPrediction.rashi || getSelectedProfileData()?.rashi || 'Calculating...'}
                      </p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                      <p className="text-[#6B6560] text-sm mb-1">Ruling Dasha</p>
                      <p className="text-[#C84B32] font-semibold text-lg">
                        {yearlyPrediction.ruling_dasha || 'Jupiter Maha Dasha'}
                      </p>
                    </div>
                    <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                      <p className="text-[#6B6560] text-sm mb-1">Year Theme</p>
                      <p className="text-[#C84B32] font-semibold text-lg">
                        {yearlyPrediction.year_theme || 'Growth & Transformation'}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-[#282622] leading-relaxed">
                    {yearlyPrediction.overview || 'Your yearly cosmic overview will appear here with personalized insights based on your birth chart and planetary transits.'}
                  </p>
                </div>
              </div>

              {/* Category Tabs */}
              <div className="flex flex-wrap gap-2 justify-center">
                <button
                  onClick={() => setActiveCategory('overview')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeCategory === 'overview'
                      ? 'bg-[#C84B32] text-white'
                      : 'bg-[#FFF9EF] text-[#6B6560] hover:bg-[#FCE9D9]'
                  }`}
                >
                  Overview
                </button>
                {Object.entries(YEAR_THEMES).map(([key, theme]) => (
                  <button
                    key={key}
                    onClick={() => setActiveCategory(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                      activeCategory === key
                        ? `bg-gradient-to-r ${theme.color} text-white`
                        : 'bg-[#FFF9EF] text-[#6B6560] hover:bg-[#FCE9D9]'
                    }`}
                    data-testid={`yearly-category-${key}`}
                  >
                    <theme.icon className="w-4 h-4" />
                    {theme.label}
                  </button>
                ))}
              </div>

              {/* Category Content */}
              {activeCategory === 'overview' ? (
                <div className="cosmic-card p-6">
                  <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-4">
                    Monthly Highlights
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {MONTHS.map((month, index) => (
                      <div key={month} className="p-4 rounded-xl bg-[#FFF9EF] hover:bg-[#FCE9D9]/60 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[#C84B32] font-medium">{month}</span>
                          <div className="flex">
                            {[...Array(Math.floor(Math.random() * 3) + 3)].map((_, i) => (
                              <Star key={i} className="w-3 h-3 text-[#C84B32] fill-[#F5B942]" />
                            ))}
                          </div>
                        </div>
                        <p className="text-[#6B6560] text-sm">
                          {yearlyPrediction.monthly?.[index] || 'Positive energy flows. Good time for new beginnings and important decisions.'}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="cosmic-card p-6">
                  <div className="flex items-center gap-3 mb-6">
                    {(() => {
                      const ThemeIcon = YEAR_THEMES[activeCategory].icon;
                      return (
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${YEAR_THEMES[activeCategory].color} flex items-center justify-center`}>
                          <ThemeIcon className="w-6 h-6 text-white" />
                        </div>
                      );
                    })()}
                    <div>
                      <h3 className="font-['Fraunces'] text-xl text-[#282622]">
                        {YEAR_THEMES[activeCategory].label}
                      </h3>
                      <p className="text-[#6B6560] text-sm">Your {selectedYear} {activeCategory} forecast</p>
                    </div>
                  </div>
                  
                  <div className="space-y-6">
                    <p className="text-[#282622] leading-relaxed">
                      {yearlyPrediction.categories?.[activeCategory]?.overview || 
                        `Your ${activeCategory} prospects for ${selectedYear} look promising. The planetary alignments indicate significant opportunities for growth and positive developments in this area of life.`}
                    </p>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                        <h4 className="text-green-600 font-medium mb-2 flex items-center gap-2">
                          <Zap className="w-4 h-4" /> Favorable Periods
                        </h4>
                        <p className="text-[#6B6560] text-sm">
                          {yearlyPrediction.categories?.[activeCategory]?.favorable || 
                            'March-April, July-August, November'}
                        </p>
                      </div>
                      <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                        <h4 className="text-orange-600 font-medium mb-2 flex items-center gap-2">
                          <Target className="w-4 h-4" /> Areas to Focus
                        </h4>
                        <p className="text-[#6B6560] text-sm">
                          {yearlyPrediction.categories?.[activeCategory]?.focus || 
                            'Planning, patience, and strategic decisions'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-4 rounded-xl bg-[#FFF9EF]">
                      <h4 className="text-[#C84B32] font-medium mb-2">Cosmic Advice</h4>
                      <p className="text-[#282622] text-sm italic">
                        "{yearlyPrediction.categories?.[activeCategory]?.advice || 
                          'Trust the cosmic timing. What is meant for you will find its way. Stay patient and maintain positive intentions.'}"
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Lucky Elements */}
              <div className="cosmic-card p-6">
                <h3 className="font-['Fraunces'] text-xl text-[#282622] mb-4">
                  Lucky Elements for {selectedYear}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                    <p className="text-[#8A8279] text-xs mb-1">Lucky Colors</p>
                    <div className="flex justify-center gap-2 mt-2">
                      {(yearlyPrediction.lucky_colors || ['Gold', 'Purple', 'White']).map((color, i) => (
                        <span key={i} className="px-2 py-1 text-xs rounded-full bg-[#C84B32]/20 text-[#C84B32]">
                          {color}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                    <p className="text-[#8A8279] text-xs mb-1">Lucky Numbers</p>
                    <p className="text-[#C84B32] font-bold text-lg mt-2">
                      {yearlyPrediction.lucky_numbers?.join(', ') || '3, 7, 9, 12'}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                    <p className="text-[#8A8279] text-xs mb-1">Lucky Days</p>
                    <p className="text-[#C84B32] font-medium mt-2">
                      {yearlyPrediction.lucky_days?.join(', ') || 'Thursday, Sunday'}
                    </p>
                  </div>
                  <div className="text-center p-4 rounded-xl bg-[#FFF9EF]">
                    <p className="text-[#8A8279] text-xs mb-1">Gemstone</p>
                    <p className="text-[#C84B32] font-medium mt-2">
                      {yearlyPrediction.gemstone || 'Yellow Sapphire'}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {/* No Profile Selected */}
        {profiles.length === 0 && !loading && (
          <div className="cosmic-card p-10 text-center">
            <Star className="w-16 h-16 text-[#C84B32] mx-auto mb-6" />
            <h3 className="font-['Fraunces'] text-2xl text-[#282622] mb-3">
              Create a Profile First
            </h3>
            <p className="text-[#6B6560] mb-6">
              Add a profile with your birth details to get your yearly forecast
            </p>
            <Button
              onClick={() => navigate('/profiles')}
              className="bg-gradient-to-r from-[#C84B32] to-[#F5B942] text-white"
            >
              Create Profile
            </Button>
          </div>
        )}
      </main>
      <ShubhBottomNav />
    </div>
  );
};

export default YearlyPredictionsPage;
